// src/app/_service/scoped-tags.service.ts
// tasks/frontend-tagging.md (Phase 2): the two tag sources a Note needs beyond the caller's own tag
// list (DataService.allTags / GET /api/tags):
//
// 1. SPACE tags of the Note's Space — GET /api/tags?spaceId=<id>. The server lists every tag
//    document in that Space's database (which, for the user's own PRIVATE Space, is the same
//    database their PRIVATE tags live in), so the result is filtered to SPACE tags of exactly that
//    Space here — another Space's tags are never offered.
// 2. The current user's PRIVATE tag assignment for a Note — GET/POST
//    /api/tags/private-assignments/<noteId>[/add|/remove]. Kept entirely separate from the Note:
//    nothing here ever touches Note.Tags or goes through Note save/sync, so a private assignment
//    can't leak into a shared Note document.
//
// Offline: both are cached read-through in localStorage (account- and data-target-scoped, like
// SpaceDirectoryService) so a Note opened offline still shows its tags. Private-assignment
// *changes* are online-only: Phase 1 has no offline/sync channel for them (per-note endpoints only,
// no changes feed), and queueing them locally would be a new sync mechanism — out of scope here.

import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, firstValueFrom, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { ApiResponse, DataService, Note, Tag } from './data.service';
import { AuthService } from './auth.service';
import { targetSuffix } from '../_util/data-target';
import { filterSpaceTags } from '../_util/tag-scope.util';

const SPACE_TAGS_KEY_PREFIX = 'notes:space_tags';
const PRIVATE_ASSIGNMENTS_KEY_PREFIX = 'notes:private_tag_assignments';
/** Set once the full assignment list has been loaded at least once (then the cached map is complete
 * as of that load, plus every per-note change made since). */
const PRIVATE_ASSIGNMENTS_COMPLETE_KEY_PREFIX = 'notes:private_tag_assignments_complete';
/** Private tags chosen for a note that isn't on the server yet (see queuePrivateTagsForNewNote). */
const PENDING_PRIVATE_TAGS_KEY_PREFIX = 'notes:pending_private_tags';

/** tasks/frontend-tagging-phase3.md — all of the current user's private tag assignments, by note id.
 * `available` is false when the server doesn't offer the bulk endpoint (and nothing complete is
 * cached): private tags then can't take part in Home filtering at all, rather than partially. */
/** Result of POST /api/tags/private-assignments/bulk/add|remove (tasks/backend-bulk-private-tag-assignments.md). */
export interface BulkPrivateTagResult {
  requested: number;
  updated: number;
  unchanged: number;
  /** The resulting full private tag list of every note processed. */
  assignments: Array<{ noteId: string; privateTagIds: string[] }>;
  notFound: string[];
  conflicts: string[];
  failed: Array<{ noteId: string; error: string }>;
}

/** `unsupported`: the server predates the bulk endpoints — the caller falls back to per-note calls. */
export type BulkPrivateTagOutcome =
  | { kind: 'ok'; result: BulkPrivateTagResult }
  | { kind: 'unsupported' }
  | { kind: 'error'; message: string };

export interface PrivateTagIndexResult {
  available: boolean;
  byNote: ReadonlyMap<string, readonly string[]>;
}

@Injectable({ providedIn: 'root' })
export class ScopedTagsService {
  private readonly baseUrl = environment.apiBaseUrl || '/api';

  constructor(
    private readonly http: HttpClient,
    private readonly dataService: DataService,
    private readonly auth: AuthService,
  ) {}

  // ---------------------------------------------------------------------------
  // Space tags
  // ---------------------------------------------------------------------------

  /** Last-known SPACE tags of `spaceId` (offline-safe; empty if never fetched on this device). */
  getCachedSpaceTags(spaceId: string | null | undefined): Tag[] {
    if (!spaceId) return [];
    return this.readMap<Tag[]>(SPACE_TAGS_KEY_PREFIX)[spaceId] ?? [];
  }

  /** Fetches the SPACE tags of `spaceId`; falls back to the cached list when offline/failing. */
  loadSpaceTags(spaceId: string | null | undefined): Observable<Tag[]> {
    if (!spaceId) return of([]);
    const url = `${this.baseUrl}/tags?spaceId=${encodeURIComponent(spaceId)}`;
    return this.http.get<ApiResponse<any[]>>(url).pipe(
      map((resp) => {
        const all = Array.isArray(resp?.data) ? resp.data.map((raw) => this.dataService.normalizeTag(raw)) : [];
        const tags = filterSpaceTags(all, spaceId);
        this.updateMap<Tag[]>(SPACE_TAGS_KEY_PREFIX, (m) => ({ ...m, [spaceId]: tags }));
        return tags;
      }),
      catchError(() => of(this.getCachedSpaceTags(spaceId))),
    );
  }

  /** Keeps the cached Space tag list current after a create/update/delete in the editor. */
  upsertCachedSpaceTag(tag: Tag): void {
    const spaceId = tag.spaceId;
    if (!spaceId) return;
    this.updateMap<Tag[]>(SPACE_TAGS_KEY_PREFIX, (m) => {
      const list = (m[spaceId] ?? []).filter((t) => t._id !== tag._id);
      return { ...m, [spaceId]: [...list, tag] };
    });
  }

  removeCachedSpaceTag(spaceId: string, tagId: string): void {
    this.updateMap<Tag[]>(SPACE_TAGS_KEY_PREFIX, (m) => ({
      ...m,
      [spaceId]: (m[spaceId] ?? []).filter((t) => t._id !== tagId),
    }));
  }

  // ---------------------------------------------------------------------------
  // Private tag assignments
  // ---------------------------------------------------------------------------

  /** Last-known private tag ids for `noteId`, or undefined if never fetched on this device. */
  getCachedPrivateTagIds(noteId: string): string[] | undefined {
    return this.readMap<string[]>(PRIVATE_ASSIGNMENTS_KEY_PREFIX)[noteId];
  }

  /** The caller's private tag ids for a note. Offline/failing: the cached ids (or none). A 404
   * (note not on the server yet, e.g. never synced) is simply "no private tags". */
  loadPrivateTagIds(noteId: string, spaceId?: string | null): Observable<string[]> {
    const query = spaceId ? `?spaceId=${encodeURIComponent(spaceId)}` : '';
    const url = `${this.baseUrl}/tags/private-assignments/${encodeURIComponent(noteId)}${query}`;
    return this.http.get<ApiResponse<{ noteId: string; privateTagIds: string[] }>>(url).pipe(
      map((resp) => this.cacheAssignment(noteId, resp?.data?.privateTagIds)),
      catchError((err: HttpErrorResponse) => of(err?.status === 404 ? [] : (this.getCachedPrivateTagIds(noteId) ?? []))),
    );
  }

  /**
   * Every private tag assignment of the current user, in ONE request — for Home filtering, which
   * must see the whole note list before paging (never one request per note).
   *
   * PROPOSED endpoint — not in Phase 1 (see tasks/frontend-tagging-phase3-notes.md):
   *   GET /api/tags/private-assignments → { status, data: [{ noteId, privateTagIds: string[] }] }
   * Until the server provides it this request 404s (it falls through to GET /api/tags/<tag_id>) and
   * the result is `available: false`. Offline, the last complete load (kept current by the per-note
   * calls above) is used.
   */
  loadPrivateTagIndex(): Observable<PrivateTagIndexResult> {
    const url = `${this.baseUrl}/tags/private-assignments`;
    return this.http.get<ApiResponse<Array<{ noteId: string; privateTagIds: string[] }>>>(url).pipe(
      map((resp) => {
        if (!Array.isArray(resp?.data)) {
          return this.cachedPrivateTagIndex();
        }
        const next: Record<string, string[]> = {};
        for (const row of resp.data) {
          const ids = Array.isArray(row?.privateTagIds) ? row.privateTagIds.filter((id) => typeof id === 'string' && !!id) : [];
          if (typeof row?.noteId === 'string' && ids.length) next[row.noteId] = ids;
        }
        this.updateMap<string[]>(PRIVATE_ASSIGNMENTS_KEY_PREFIX, () => next);
        this.updateMap<boolean>(PRIVATE_ASSIGNMENTS_COMPLETE_KEY_PREFIX, () => ({ complete: true }));
        return { available: true, byNote: new Map(Object.entries(next)) };
      }),
      catchError((err: HttpErrorResponse) => {
        // 404/405: the server has no bulk endpoint — don't pretend a partial cache is the full picture.
        if (err?.status === 404 || err?.status === 405) {
          this.updateMap<boolean>(PRIVATE_ASSIGNMENTS_COMPLETE_KEY_PREFIX, () => ({}));
          return of({ available: false, byNote: new Map<string, readonly string[]>() });
        }
        return of(this.cachedPrivateTagIndex());
      }),
    );
  }

  /** The last complete bulk load, from the offline cache — lets Home render private tags instantly. */
  cachedPrivateTagIndex(): PrivateTagIndexResult {
    if (!this.readMap<boolean>(PRIVATE_ASSIGNMENTS_COMPLETE_KEY_PREFIX)['complete']) {
      return { available: false, byNote: new Map() };
    }
    return { available: true, byNote: new Map(Object.entries(this.readMap<string[]>(PRIVATE_ASSIGNMENTS_KEY_PREFIX))) };
  }

  // ---------------------------------------------------------------------------
  // Private tags for a note that isn't on the server yet
  // ---------------------------------------------------------------------------
  // A private tag assignment needs the note to exist on the server, but a new note only gets there
  // through the sync push. Tags chosen for it before then (e.g. the calendar tag of a note created
  // from the Calendar view) are queued here — on this device, offline-safe — and assigned by
  // flushPendingPrivateTags(), which the sync runs after every push.

  queuePrivateTagsForNewNote(noteId: string, tagIds: string[]): void {
    const ids = [...new Set((tagIds ?? []).filter((id) => typeof id === 'string' && !!id))];
    if (!noteId || !ids.length) return;
    this.updateMap<string[]>(PENDING_PRIVATE_TAGS_KEY_PREFIX, (m) => ({
      ...m,
      [noteId]: [...new Set([...(m[noteId] ?? []), ...ids])],
    }));
  }

  /** Every note's queued private tag ids (one storage read — for per-note lookups in a loop). */
  getAllPendingPrivateTagIds(): Record<string, string[]> {
    return this.readMap<string[]>(PENDING_PRIVATE_TAGS_KEY_PREFIX);
  }

  /** Private tag ids still waiting for `noteId` to reach the server (shown as its private tags meanwhile). */
  getPendingPrivateTagIds(noteId: string): string[] {
    return this.readMap<string[]>(PENDING_PRIVATE_TAGS_KEY_PREFIX)[noteId] ?? [];
  }

  /**
   * Assigns queued private tags for every note that has reached the server since. `getLocalNote`
   * reads the note from this device: still new / a local draft → wait; gone → drop the entry.
   * A transient failure keeps the entry for the next sync; a refusal (4xx) drops it.
   */
  async flushPendingPrivateTags(
    getLocalNote: (noteId: string) => Promise<Pick<Note, 'spaceId' | 'syncStatus' | 'localDraft'> | undefined>,
  ): Promise<void> {
    const pending = this.readMap<string[]>(PENDING_PRIVATE_TAGS_KEY_PREFIX);
    for (const [noteId, tagIds] of Object.entries(pending)) {
      const note = await getLocalNote(noteId);
      const drop = () =>
        this.updateMap<string[]>(PENDING_PRIVATE_TAGS_KEY_PREFIX, (m) => {
          const { [noteId]: _removed, ...rest } = m;
          return rest;
        });
      if (!note || note.syncStatus === 'deleted') {
        drop();
        continue;
      }
      if (note.syncStatus === 'new' || note.localDraft) continue; // not on the server yet
      const resp = await firstValueFrom(this.addPrivateTags(noteId, tagIds, note.spaceId ?? null));
      const code = resp.error?.code ?? 0;
      if (resp.status === 'success' || (code >= 400 && code < 500)) drop();
    }
  }

  /** Adds/removes the same private tags on many notes in ONE request. */
  bulkMutatePrivateTags(op: 'add' | 'remove', noteIds: string[], tagIds: string[]): Observable<BulkPrivateTagOutcome> {
    const url = `${this.baseUrl}/tags/private-assignments/bulk/${op}`;
    return this.http.post<ApiResponse<BulkPrivateTagResult>>(url, { noteIds, tagIds }).pipe(
      map((resp): BulkPrivateTagOutcome => {
        const result = resp?.data;
        if (!result || !Array.isArray(result.assignments)) {
          return { kind: 'error', message: 'Unexpected response from the server.' };
        }
        for (const row of result.assignments) {
          this.cacheAssignment(row.noteId, row.privateTagIds);
        }
        return { kind: 'ok', result };
      }),
      catchError((err: HttpErrorResponse) => {
        const invalid = err?.error?.error?.details?.invalid_tag_ids;
        // An older server has no bulk route: the path falls through to the per-note route (note id
        // "bulk" → 404 note not found) or has no POST handler (405).
        if ((err?.status === 404 && !Array.isArray(invalid)) || err?.status === 405) {
          return of<BulkPrivateTagOutcome>({ kind: 'unsupported' });
        }
        return of<BulkPrivateTagOutcome>({ kind: 'error', message: this.assignmentErrorMessage(err) });
      }),
    );
  }

  addPrivateTags(noteId: string, tagIds: string[], spaceId?: string | null): Observable<ApiResponse<string[]>> {
    return this.mutateAssignment('add', noteId, tagIds, spaceId);
  }

  removePrivateTags(noteId: string, tagIds: string[], spaceId?: string | null): Observable<ApiResponse<string[]>> {
    return this.mutateAssignment('remove', noteId, tagIds, spaceId);
  }

  private mutateAssignment(
    op: 'add' | 'remove',
    noteId: string,
    tagIds: string[],
    spaceId?: string | null,
  ): Observable<ApiResponse<string[]>> {
    const url = `${this.baseUrl}/tags/private-assignments/${encodeURIComponent(noteId)}/${op}`;
    const body: { tagIds: string[]; spaceId?: string } = { tagIds };
    if (spaceId) body.spaceId = spaceId;
    return this.http.post<ApiResponse<{ noteId: string; privateTagIds: string[] }>>(url, body).pipe(
      map((resp) => ({ status: 'success' as const, data: this.cacheAssignment(noteId, resp?.data?.privateTagIds) })),
      catchError((err: HttpErrorResponse) =>
        of<ApiResponse<string[]>>({
          status: 'error',
          error: { code: err?.status ?? 0, message: this.assignmentErrorMessage(err) },
        }),
      ),
    );
  }

  /** The server answers 404 for two different reasons — tell them apart instead of assuming one. */
  private assignmentErrorMessage(err: HttpErrorResponse): string {
    const serverError = err?.error?.error;
    const invalidTagIds: unknown = serverError?.details?.invalid_tag_ids;
    if (err?.status === 404 && Array.isArray(invalidTagIds) && invalidTagIds.length) {
      // The tag isn't recorded as one of the user's own PRIVATE tags on the server — e.g. data the
      // Phase 1 tag migration (migrate_tags_to_scoped.py) hasn't processed yet.
      return 'The server doesn’t recognise this as one of your private tags (its tags may not have been migrated yet), so it can’t be added.';
    }
    if (err?.status === 404 && /note not found/i.test(String(serverError?.message ?? ''))) {
      return 'This note isn’t on the server yet — private tags can be added once it has synced.';
    }
    return serverError?.message ?? err?.error?.message ?? err?.message ?? 'Failed to update private tags.';
  }

  private cacheAssignment(noteId: string, ids: unknown): string[] {
    const clean = Array.isArray(ids) ? ids.filter((id): id is string => typeof id === 'string' && !!id) : [];
    this.updateMap<string[]>(PRIVATE_ASSIGNMENTS_KEY_PREFIX, (m) => {
      const next = { ...m };
      if (clean.length) next[noteId] = clean;
      else delete next[noteId];
      return next;
    });
    return clean;
  }

  // ---------------------------------------------------------------------------
  // localStorage (account/data-target scoped)
  // ---------------------------------------------------------------------------

  private key(prefix: string): string {
    return `${prefix}${targetSuffix()}::${this.auth.getUsername() ?? 'anonymous'}`;
  }

  private readMap<T>(prefix: string): Record<string, T> {
    try {
      const raw = localStorage.getItem(this.key(prefix));
      const parsed = raw ? JSON.parse(raw) : {};
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }

  private updateMap<T>(prefix: string, fn: (m: Record<string, T>) => Record<string, T>): void {
    try {
      localStorage.setItem(this.key(prefix), JSON.stringify(fn(this.readMap<T>(prefix))));
    } catch {
      // Storage full/unavailable — only the offline read cache is lost; the server stays authoritative.
    }
  }
}
